import type {Dispatch, ReactNode, RefObject, SetStateAction} from "react";
import {useCallback, useRef, useState} from "react";
import {Group, Layer, Rect, Stage} from "react-konva";
import {useResizeObserver} from "usehooks-ts";
import Konva from "konva";
import type {Image} from "@/images/Image.ts";
import type {GridPoint, LevelGeometryDocument} from "@/dungeons/model/DungeonGeometry.ts";
import type {SelectedGeometryFeature} from "@/dungeons/editor/geometry/GeometryEditorTypes.ts";
import DungeonMapLayers from "./DungeonMapLayers.tsx";
import {
    DungeonViewport,
    dungeonViewportTransform,
    fitDungeonViewport,
    panDungeonViewport,
    zoomDungeonViewport,
} from "./DungeonViewport.ts";

export type CanvasPointerEvent = {
    point: GridPoint;
    pixelsPerCell: number;
    event: Konva.KonvaEventObject<MouseEvent>;
};

export type CanvasPanButton = "primary" | "middle" | "secondary";

const mouseButton: Record<number, CanvasPanButton | undefined> = {
    0: "primary",
    1: "middle",
    2: "secondary",
};

export default function DungeonCanvasViewport({
    image,
    geometry,
    viewport,
    onViewportChange,
    panButtons,
    hiddenFeature,
    showAnnotations,
    onMapClick,
    onMapDoubleClick,
    onMapMove,
    onMapLeave,
    onContextMenu,
    renderInteraction,
    renderOverlay,
    renderLighting,
}: {
    image: Image;
    geometry: LevelGeometryDocument;
    viewport?: DungeonViewport;
    onViewportChange: Dispatch<SetStateAction<DungeonViewport | undefined>>;
    panButtons: CanvasPanButton[];
    hiddenFeature?: SelectedGeometryFeature;
    showAnnotations?: boolean;
    onMapClick?: (event: CanvasPointerEvent) => void;
    onMapDoubleClick?: (event: CanvasPointerEvent) => void;
    onMapMove?: (event: CanvasPointerEvent) => void;
    onMapLeave?: () => void;
    onContextMenu?: (event: Konva.KonvaEventObject<MouseEvent>) => void;
    renderInteraction?: (zoom: number) => ReactNode;
    renderLighting?: (transform: {x: number; y: number; scaleX: number; scaleY: number},
                      size: {width: number; height: number}) => ReactNode;
    renderOverlay?: (transform: {x: number; y: number; scaleX: number; scaleY: number},
                     size: {width: number; height: number}) => ReactNode;
}) {
    const containerRef = useRef<HTMLDivElement>(null);
    const panStart = useRef<GridPoint | undefined>(undefined);
    const moved = useRef(false);
    const [size, setSize] = useState({width: 1, height: 1});

    const handleResize = useCallback((next: {width: number | undefined; height?: number | undefined}) => {
        if (!next.width || !next.height) return;
        const measured = {width: next.width, height: next.height};
        setSize(measured);
        onViewportChange(current => current ?? fitDungeonViewport(image, geometry.registration, measured));
    }, [geometry.registration, image, onViewportChange]);

    useResizeObserver({
        ref: containerRef as RefObject<HTMLDivElement>,
        box: "content-box",
        onResize: handleResize,
    });

    const currentViewport = viewport ?? fitDungeonViewport(image, geometry.registration, size);
    const transform = dungeonViewportTransform(currentViewport, geometry.registration, size);
    const toImagePoint = (stage: Konva.Stage): GridPoint | undefined => {
        const pointer = stage.getPointerPosition();
        if (!pointer) return undefined;
        return {
            x: (pointer.x - transform.x) / transform.scaleX,
            y: (pointer.y - transform.y) / transform.scaleY,
        };
    };
    const getMapPointerEvent = (event: Konva.KonvaEventObject<MouseEvent>): CanvasPointerEvent | undefined => {
        const stage = event.target.getStage();
        if (!stage) return undefined;
        const point = toImagePoint(stage);
        if (!point || point.x < 0 || point.y < 0 || point.x > image.width || point.y > image.height) {
            return undefined;
        }
        return {point, pixelsPerCell: currentViewport.pixelsPerCell, event};
    };
    const startPan = (event: Konva.KonvaEventObject<MouseEvent>) => {
        const button = mouseButton[event.evt.button];
        if (!button || !panButtons.includes(button)) {
            moved.current = false;
            if (event.evt.button === 2) event.evt.preventDefault();
            return;
        }
        const pointer = event.target.getStage()?.getPointerPosition();
        if (!pointer) return;
        panStart.current = pointer;
        moved.current = false;
        event.evt.preventDefault();
    };
    const pan = (event: Konva.KonvaEventObject<MouseEvent>) => {
        const previous = panStart.current;
        const pointer = event.target.getStage()?.getPointerPosition();
        if (!pointer) return;
        if (previous) {
            const delta = {x: pointer.x - previous.x, y: pointer.y - previous.y};
            if (Math.abs(delta.x) + Math.abs(delta.y) > 1) moved.current = true;
            panStart.current = pointer;
            onViewportChange(current => panDungeonViewport(current ?? currentViewport, delta));
            return;
        }
        const pointerEvent = getMapPointerEvent(event);
        if (pointerEvent) onMapMove?.(pointerEvent);
        else onMapLeave?.();
    };
    const stopPan = () => {
        panStart.current = undefined;
    };
    const click = (event: Konva.KonvaEventObject<MouseEvent>) => {
        if (moved.current || event.evt.button !== 0 || !onMapClick) return;
        const pointerEvent = getMapPointerEvent(event);
        if (pointerEvent) onMapClick(pointerEvent);
    };
    const doubleClick = (event: Konva.KonvaEventObject<MouseEvent>) => {
        if (moved.current || event.evt.button !== 0 || !onMapDoubleClick) return;
        const pointerEvent = getMapPointerEvent(event);
        if (pointerEvent) onMapDoubleClick(pointerEvent);
    };
    const contextMenu = (event: Konva.KonvaEventObject<MouseEvent>) => {
        event.evt.preventDefault();
        onContextMenu?.(event);
    };
    const zoom = (event: Konva.KonvaEventObject<WheelEvent>) => {
        event.evt.preventDefault();
        const pointer = event.target.getStage()?.getPointerPosition();
        if (!pointer) return;
        const factor = event.evt.deltaY > 0 ? 1 / 1.05 : 1.05;
        onViewportChange(current => zoomDungeonViewport(current ?? currentViewport, pointer, size, factor));
    };

    return <div ref={containerRef} className="relative size-full overflow-hidden bg-neutral-950">
        <Stage width={size.width} height={size.height} onWheel={zoom} onMouseDown={startPan} onMouseMove={pan}
               onMouseUp={stopPan} onMouseLeave={() => { stopPan(); onMapLeave?.(); }}
               onClick={click} onDblClick={doubleClick} onContextMenu={contextMenu}>
            <DungeonMapLayers image={image} geometry={geometry} transform={transform} zoom={transform.scaleX}
                              lighting={renderLighting?.(transform, size)}
                              hiddenFeature={hiddenFeature} showAnnotations={showAnnotations}/>
            <Layer><Group {...transform}>
                <Rect width={image.width} height={image.height} fill="rgba(0,0,0,0.001)" name="editor-map-hit"/>
                {renderInteraction?.(transform.scaleX)}
            </Group></Layer>
        </Stage>
        {renderOverlay?.(transform, size)}
    </div>;
}
